import {
  deleteV1CompaniesCompanyIdFileAttachmentsId,
  FileAttachableType,
  FileAttachment_Read,
  FileAttachmentCategory,
  getV1CompaniesCompanyIdComplianceDocumentsComplianceDocumentIdFileAttachments,
  getV1CompaniesCompanyIdDriverDaysDriverDayIdFileAttachments,
  getV1CompaniesCompanyIdJobsJobIdFileAttachments,
  getV1CompaniesCompanyIdLoadsLoadIdFileAttachments,
  getV1CompaniesCompanyIdOrdersOrderIdFileAttachments,
  getV1CompaniesCompanyIdProjectsProjectIdFileAttachments,
  getV1CompaniesCompanyIdSettlementsSettlementIdFileAttachments,
  patchV1CompaniesCompanyIdFileAttachmentsId,
  postV1CompaniesCompanyIdComplianceDocumentsComplianceDocumentIdFileAttachments,
  postV1CompaniesCompanyIdDriverDaysDriverDayIdFileAttachments,
  postV1CompaniesCompanyIdJobsJobIdFileAttachments,
  postV1CompaniesCompanyIdLoadsLoadIdFileAttachments,
  postV1CompaniesCompanyIdOrdersOrderIdFileAttachments,
  postV1CompaniesCompanyIdProjectsProjectIdFileAttachments,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { Dispatch, SetStateAction } from 'react';

import { useDirectUploads } from '~hooks/useDirectUploads/useDirectUploads';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { isUploadValidationError } from '~utils/extractAxiosError';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { FileAttachment } from './models';

interface UpdateFileAttachmentParams {
  description?: string;
  category?: FileAttachmentCategory;
}

type Callback =
  | Dispatch<SetStateAction<FileAttachment>>
  | ((FileAttachment: FileAttachment) => void);

type DirectUploadCreateMethod<TPath extends Record<string, string>> = (options: {
  path: TPath;
  body: { file: string };
}) => PromiseLike<{ data: { data: FileAttachment_Read } }>;

interface CreateFileAttachmentOptions {
  /**
   * When true, a backend file-validation rejection (see
   * `isUploadValidationError`) re-throws without the generic toast so the
   * caller can render the reason inline next to its upload control. Leave
   * unset for callers with no inline error surface — they keep the
   * toast/Sentry pipeline for every failure.
   */
  surfaceValidationErrorInline?: boolean;
}

export const useFileAttachment = () => {
  const { createDirectUpload } = useDirectUploads();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  const getAllFileAttachmentsById = async (
    fileAttachableId: string,
    fileAttachableType: FileAttachableType,
    signal?: AbortSignal,
  ) => {
    const fetchByType = () => {
      switch (fileAttachableType) {
        case FileAttachableType.JOB:
          return getV1CompaniesCompanyIdJobsJobIdFileAttachments({
            path: { 'company-id': companyId, 'job-id': fileAttachableId },
            signal,
          });
        case FileAttachableType.PROJECT:
          return getV1CompaniesCompanyIdProjectsProjectIdFileAttachments({
            path: { 'company-id': companyId, 'project-id': fileAttachableId },
            signal,
          });
        case FileAttachableType.ORDER:
          return getV1CompaniesCompanyIdOrdersOrderIdFileAttachments({
            path: { 'company-id': companyId, 'order-id': fileAttachableId },
            signal,
          });
        case FileAttachableType.LOAD:
          return getV1CompaniesCompanyIdLoadsLoadIdFileAttachments({
            path: { 'company-id': companyId, 'load-id': fileAttachableId },
            signal,
          });
        case FileAttachableType.DRIVER_DAY:
          return getV1CompaniesCompanyIdDriverDaysDriverDayIdFileAttachments({
            path: { 'company-id': companyId, 'driver-day-id': fileAttachableId },
            signal,
          });
        case FileAttachableType.SETTLEMENT:
          return getV1CompaniesCompanyIdSettlementsSettlementIdFileAttachments({
            path: { 'company-id': companyId, 'settlement-id': fileAttachableId },
            signal,
          });
        default:
          throw new Error(`Unsupported file attachable type: ${fileAttachableType}`);
      }
    };

    try {
      const response = await fetchByType();
      return response.data.data.map((item) => FileAttachment.parse(item));
    } catch (error) {
      if (signal?.aborted) {
        throw error;
      }

      // A load can be deleted out from under an open ticket drawer (removed in
      // another tab before the RTU prunes the local cache); the resulting 404 is
      // expected, not actionable. Scoped to LOAD so other types still surface
      // 404s. See HORIZON-FE-2MM.
      connection.handleRequestError(
        error,
        t('error_messages.file_attachment.failed_to_fetch'),
        fileAttachableType === FileAttachableType.LOAD ? [404] : undefined,
      );
      throw error;
    }
  };

  const getAllFileAttachmentsByJobId = async (jobId: string, signal?: AbortSignal) => {
    return await getAllFileAttachmentsById(jobId, FileAttachableType.JOB, signal);
  };

  const getFileAttachmentByDriverDayId = async (driverDayId: string) => {
    try {
      const response = await getV1CompaniesCompanyIdDriverDaysDriverDayIdFileAttachments({
        path: { 'company-id': companyId, 'driver-day-id': driverDayId },
      });
      return response.data.data.map((item) => FileAttachment.parse(item));
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.file_attachment.failed_to_fetch'),
      );
      throw error;
    }
  };

  const updateFileAttachmentById = async (
    id: string,
    params: UpdateFileAttachmentParams,
  ) => {
    try {
      const response = await patchV1CompaniesCompanyIdFileAttachmentsId({
        path: { 'company-id': companyId, id },
        body: { ...params },
      });
      return FileAttachment.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.file_attachment.failed_to_update'),
      );
      throw error;
    }
  };

  const deleteFileAttachmentById = async (id: string) => {
    try {
      await deleteV1CompaniesCompanyIdFileAttachmentsId({
        path: { 'company-id': companyId, id },
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.file_attachment.failed_to_delete'),
      );
      throw error;
    }
  };

  // this seems to only be called from FileAttachments.tsx
  const createFileAttachment = async (
    file: File,
    attachableId: string,
    attachableType: FileAttachableType,
    options?: CreateFileAttachmentOptions,
  ) => {
    switch (attachableType) {
      case FileAttachableType.PROJECT:
        return await createFileAttachmentOnProject(
          file,
          attachableId,
          undefined,
          options,
        );
      case FileAttachableType.ORDER:
        return await createFileAttachmentOnOrder(file, attachableId, undefined, options);
      case FileAttachableType.JOB:
        return await createFileAttachmentOnJob(file, attachableId, undefined, options);
      case FileAttachableType.LOAD:
        return await createFileAttachmentOnLoad(file, attachableId, undefined, options);
      case FileAttachableType.DRIVER_DAY:
        return await createFileAttachmentOnDriverDay(file, attachableId, options);
      default:
        throw new Error('Invalid attachable type');
    }
  };

  const createFileAttachmentWithDirectUpload = async <
    TPath extends Record<string, string>,
  >(
    file: File,
    serviceMethod: DirectUploadCreateMethod<TPath>,
    path: TPath,
    callBack?: Callback,
    options?: CreateFileAttachmentOptions,
  ) => {
    const directUpload = await createDirectUpload(file);
    try {
      const response = await serviceMethod({
        path,
        body: { file: directUpload.signed_id },
      });
      const data = FileAttachment.parse(response.data.data);
      callBack?.(data);
      return data;
    } catch (error) {
      // Only callers that opted in (they render the rejection reason inline
      // next to their upload control) skip the generic toast for a backend
      // file-validation rejection; everyone else keeps the toast for every
      // failure.
      if (options?.surfaceValidationErrorInline && isUploadValidationError(error)) {
        throw error;
      }
      connection.handleRequestError(
        error,
        t('error_messages.file_attachment.failed_to_create'),
      );
      throw error;
    }
  };

  const createFileAttachmentOnProject = async (
    file: File,
    projectId: string,
    callBack?: Callback,
    options?: CreateFileAttachmentOptions,
  ) => {
    return await createFileAttachmentWithDirectUpload(
      file,
      postV1CompaniesCompanyIdProjectsProjectIdFileAttachments,
      { 'company-id': companyId, 'project-id': projectId },
      callBack,
      options,
    );
  };

  const createFileAttachmentOnOrder = async (
    file: File,
    orderId: string,
    callBack?: Callback,
    options?: CreateFileAttachmentOptions,
  ) => {
    return await createFileAttachmentWithDirectUpload(
      file,
      postV1CompaniesCompanyIdOrdersOrderIdFileAttachments,
      { 'company-id': companyId, 'order-id': orderId },
      callBack,
      options,
    );
  };

  const createFileAttachmentOnJob = async (
    file: File,
    jobId: string,
    callBack?: Callback,
    options?: CreateFileAttachmentOptions,
  ) => {
    return await createFileAttachmentWithDirectUpload(
      file,
      postV1CompaniesCompanyIdJobsJobIdFileAttachments,
      { 'company-id': companyId, 'job-id': jobId },
      callBack,
      options,
    );
  };

  // afaict this code is currently unused but it _could_ be called via createFileAttachment :/
  const createFileAttachmentOnLoad = async (
    file: File,
    loadId: string,
    callBack?: Callback,
    options?: CreateFileAttachmentOptions,
  ) => {
    return await createFileAttachmentWithDirectUpload(
      file,
      postV1CompaniesCompanyIdLoadsLoadIdFileAttachments,
      { 'company-id': companyId, 'load-id': loadId },
      callBack,
      options,
    );
  };

  const createFileAttachmentOnDriverDay = async (
    file: File,
    driverDayId: string,
    options?: CreateFileAttachmentOptions,
  ) => {
    return await createFileAttachmentWithDirectUpload(
      file,
      postV1CompaniesCompanyIdDriverDaysDriverDayIdFileAttachments,
      { 'company-id': companyId, 'driver-day-id': driverDayId },
      undefined,
      options,
    );
  };

  const getFileAttachmentsByComplianceDocumentId = async (
    complianceDocumentId: string,
  ) => {
    try {
      const response =
        await getV1CompaniesCompanyIdComplianceDocumentsComplianceDocumentIdFileAttachments(
          {
            path: {
              'company-id': companyId,
              'compliance-document-id': complianceDocumentId,
            },
          },
        );
      return response.data.data.map((item) => FileAttachment.parse(item));
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.file_attachment.failed_to_fetch'),
      );
      throw error;
    }
  };

  const createFileAttachmentOnComplianceDocument = async (
    file: File,
    complianceDocumentId: string,
  ) => {
    try {
      const directUpload = await createDirectUpload(file);
      const response =
        await postV1CompaniesCompanyIdComplianceDocumentsComplianceDocumentIdFileAttachments(
          {
            path: {
              'company-id': companyId,
              'compliance-document-id': complianceDocumentId,
            },
            body: { file: directUpload.signed_id },
          },
        );
      return FileAttachment.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.file_attachment.failed_to_create'),
      );
      throw error;
    }
  };

  return {
    getAllFileAttachmentsById,
    getAllFileAttachmentsByJobId,
    getFileAttachmentByDriverDayId,
    getFileAttachmentsByComplianceDocumentId,
    createFileAttachment,
    createFileAttachmentOnJob,
    createFileAttachmentOnDriverDay,
    createFileAttachmentOnComplianceDocument,
    updateFileAttachmentById,
    deleteFileAttachmentById,
  };
};
